import React, { useMemo, useState } from "react";

export type AcnsPaletteTerm = {
  code: string;
  label: string;
  category: string;
  severity: string;
  shortcut: string;
};

export const ACNS_ANNOTATION_PALETTE: readonly AcnsPaletteTerm[] = [
  { code: "LPD", label: "Lateralized periodic discharges", category: "periodic", severity: "high", shortcut: "lpd" },
  { code: "GPD", label: "Generalized periodic discharges", category: "periodic", severity: "high", shortcut: "gpd" },
  { code: "LRDA", label: "Lateralized rhythmic delta activity", category: "rhythmic", severity: "moderate", shortcut: "lrda" },
  { code: "GRDA", label: "Generalized rhythmic delta activity", category: "rhythmic", severity: "moderate", shortcut: "grda" },
  { code: "BIRDS", label: "Brief potentially ictal rhythmic discharges", category: "ictal", severity: "high", shortcut: "birds" },
  { code: "ESZ", label: "Electrographic seizure", category: "ictal", severity: "critical", shortcut: "esz" },
  { code: "ECSE", label: "Electrographic status epilepticus", category: "ictal", severity: "critical", shortcut: "ecse" },
  { code: "SIRPIDS", label: "Stimulus-induced rhythmic, periodic, or ictal discharges", category: "special", severity: "high", shortcut: "sirp" },
  { code: "SUPPRESSION", label: "Suppression / burst-suppression", category: "background", severity: "high", shortcut: "supp" },
];

export type AcnsAnnotationPaletteProps = {
  onSelectTerm: (term: AcnsPaletteTerm) => void;
};

export default function AcnsAnnotationPalette({ onSelectTerm }: AcnsAnnotationPaletteProps) {
  const [query, setQuery] = useState("");
  const terms = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return ACNS_ANNOTATION_PALETTE;
    return ACNS_ANNOTATION_PALETTE.filter(
      (term) =>
        term.code.toLowerCase().includes(needle) ||
        term.label.toLowerCase().includes(needle) ||
        term.shortcut.toLowerCase().includes(needle),
    );
  }, [query]);

  return (
    <section data-testid="acns-annotation-palette" aria-label="ACNS critical-care annotation palette">
      <h3 className="h6">ACNS critical-care terms</h3>
      <input
        className="form-control form-control-sm mb-2"
        placeholder="Search code, shortcut, or label"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Filter ACNS terms"
      />
      <div className="d-flex flex-wrap gap-2">
        {terms.map((term) => (
          <button
            key={term.code}
            type="button"
            className="btn btn-sm btn-outline-primary"
            title={`${term.label} (${term.shortcut})`}
            data-testid={`acns-term-${term.code}`}
            onClick={() => onSelectTerm(term)}
          >
            {term.code}
          </button>
        ))}
      </div>
    </section>
  );
}
